import type { DroneLive } from '@/lib/store/drones';

const DRONE_SVG_BASE = 44;
const ICON_STEP = 0.12;
function droneSize(step: number) {
  return Math.round(DRONE_SVG_BASE * (1 + step * ICON_STEP));
}

// ENG-166 — z-index tiers so the currently hovered/selected marker's icon +
// label always render above a neighbour's, once one has been resolved.
// Hover outranks selected (so it's always visible even over a selected
// neighbour) — a different precedence than size (selected wins there, see
// `sz` below) and glow (both stack). These three rules are deliberately not
// unified into one — each visual property has its own sensible tradeoff —
// but all three are computed here, in `buildDroneMarkerHtml`'s single
// return value, rather than one of them (z-index) being hand-duplicated
// separately in drones.ts where it could drift out of sync.
export function droneZIndex(selected: boolean, hovered: boolean): string {
  if (hovered) return '20';
  if (selected) return '10';
  return '0';
}

export type DroneIconStatus = DroneLive['telemetry']['status'];

export interface DroneIconInput {
  status: DroneIconStatus;
  callsign: string;
  heading: number;
  /** Cross-tenant provenance — external drones render grey. */
  origin?: DroneLive['origin'];
  partnerTenant?: string | null;
}

// Cross-tenant grey (matches Doc "Partner: B" grey marker + mockup external).
const EXTERNAL_COLOR = '#6b7280';

/** This marker is built as an HTML string and assigned to `innerHTML`.
 *  `partnerTenant` is authored by a *different* tenant and reaches us over
 *  MQTT, and `callsign` is publisher-controlled too — neither is ours to
 *  trust as markup. */
export function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );
}

// Doc 007 — Drone Marker Color Logic. (moved verbatim from drones.ts)
function statusColor(status: DroneIconStatus): string {
  switch (status) {
    case 'LOST':
      return '#c41a1a';
    case 'SA':
    case 'DAA-DEG':
      return '#d97706';
    case 'Active':
    case 'DAA':
      return '#22a050';
    default:
      return '#6b7280';
  }
}

function animationClass(status: DroneIconStatus): string {
  switch (status) {
    case 'DAA':
      return 'fly-drone-daa';
    case 'SA':
    case 'DAA-DEG':
      return 'fly-drone-warn';
    default:
      return 'fly-drone-pulse';
  }
}

/** Build the drone marker inner HTML + the fixed box size. Moved verbatim
 *  from drones.ts `buildInnerHtml`; only the input shape changed (plain
 *  fields instead of DroneLive) so the DS map can reuse it. */
export function buildDroneMarkerHtml(
  input: DroneIconInput,
  selected: boolean,
  hovered: boolean,
  sizeStep: number,
): { html: string; boxSize: number; zIndex: string } {
  const { status, callsign, heading: rawHeading, origin = 'internal', partnerTenant } = input;
  const external = origin !== 'internal';
  const badge = external ? EXTERNAL_COLOR : statusColor(status);
  // External drones don't carry our status taxonomy — badge them by provenance.
  const label = external ? (origin === 'partner' ? 'PARTNER' : 'NET-ID') : (status ?? 'Active');
  const isLost = !external && status === 'LOST';
  const animClass = external ? 'fly-drone-pulse' : animationClass(status);
  // Phase-locked to the wall clock rather than starting fresh at 0% —
  // drones.ts rebuilds this SVG's innerHTML on every status/selection/hover
  // change (hover fires far more often than the others), and a freshly
  // created element always starts its animation at 0%, snapping the pulse
  // glow back to its dimmest point on every rebuild. A negative delay equal
  // to how far into the cycle "now" is makes a rebuilt element pick up
  // exactly where the old one would have been, so rebuilds are seamless.
  const ANIM_CYCLE_S = 2.5;
  const animPhase = ((Date.now() / 1000) % ANIM_CYCLE_S).toFixed(3);
  const base = droneSize(sizeStep);
  // Selected wins over hovered rather than stacking (a selected-and-hovered
  // drone stays at 1.18x, not larger still) — the hover glow already carries
  // the extra emphasis, and compounding growth would make it disproportionate
  // next to its neighbours.
  const sz = Math.round(base * (selected ? 1.18 : hovered ? 1.15 : 1));
  // The wrap is a fixed square sized to fit the SVG at any heading
  // (Math.ceil of the SVG's diagonal). This keeps the marker's axis-aligned
  // bbox constant so anchor:'center' lands on the SVG centre under rotation.
  const boxSize = Math.ceil(sz * Math.SQRT2);
  // Doc 007 — "DRONE.status = 'LOST' → red fill, NO heading indicator".
  // We swap the rotating chevron for a static red disc so the position is
  // still anchored on the map but no longer asserts a direction we don't
  // know. Animation is also suppressed — LOST is terminal, an attention-
  // grabbing pulse would be misleading once nothing else changes.
  const heading = isLost ? 0 : (rawHeading ?? 0);
  const droneFillColor = isLost ? '#c41a1a' : external ? EXTERNAL_COLOR : '#22a050';
  // Selected and hovered glows stack (both can be true at once) rather than
  // one replacing the other — ENG-166's hover state stays visible even on
  // the currently-selected drone. Hover uses a small blue glow that appears
  // nowhere else on the map (status colours are green/amber/red, rings are
  // red/orange) — a single soft white blur was tried first and nearly
  // disappeared against the light basemap; a wider two-layer blue halo fixed
  // that but read as too heavy, so this settles on a small single layer.
  //
  // Set as the `--emphasis-glow` custom property, not a plain `filter:`
  // value: the pulse/DAA/warn keyframes in index.css animate `filter` on
  // this same element, and a CSS animation's animated value overrides ANY
  // other `filter` declaration — including inline style — for as long as it
  // runs. A plain `filter:drop-shadow(...)` here would render for LOST
  // drones (no competing animation) but be silently invisible on every
  // other status. The keyframes reference `var(--emphasis-glow, )` so the
  // two compose instead of one clobbering the other.
  const glows = [
    selected ? `drop-shadow(0 0 8px ${badge})` : '',
    hovered ? 'drop-shadow(0 0 4px #3b82f6)' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const glowStyle = `--emphasis-glow:${glows};filter:var(--emphasis-glow, );`;
  // `data-hit` marks the one element per marker that's actually clickable/
  // hoverable — the visible shape, never the padded box around it (ENG-166:
  // an invisible padding overlap was stealing clicks meant for a neighbour's
  // icon underneath it). `pointer-events:auto` re-enables hit-testing here
  // against the container's `pointer-events:none` (set in drones.ts).
  const visual = isLost
    ? `<div data-hit style="position:absolute;left:50%;top:50%;` +
      `width:${Math.round(sz * 0.7)}px;height:${Math.round(sz * 0.7)}px;` +
      `transform:translate(-50%,-50%);border-radius:50%;` +
      `background:rgba(196,26,26,.55);` +
      `border:3px solid #c41a1a;` +
      glowStyle +
      `box-shadow:inset 0 0 0 2px rgba(255,255,255,.85),0 1px 4px rgba(0,0,0,.4);` +
      `pointer-events:auto;cursor:pointer"></div>`
    : // The rotating SVG — absolutely centered, rotated by heading; box stays fixed.
      `<svg data-hit width="${sz}" height="${sz}" viewBox="0 0 44 44" fill="none" ` +
      `style="position:absolute;left:50%;top:50%;display:block;` +
      `transform:translate(-50%,-50%) rotate(${heading}deg);transform-origin:center;` +
      `animation:${animClass} ${ANIM_CYCLE_S}s ease-in-out infinite;animation-delay:-${animPhase}s;` +
      `pointer-events:auto;cursor:pointer;` +
      glowStyle +
      `">` +
      `<polygon points="22,3 40,41 22,33 4,41" fill="${droneFillColor}"/>` +
      `<polygon points="22,9 36,39 22,31 8,39" fill="rgba(255,255,255,.15)"/>` +
      `</svg>`;
  const html =
    // Callsign pill — absolute above box. Doesn't rotate or affect box.
    `<div style="position:absolute;left:50%;bottom:100%;transform:translateX(-50%);margin-bottom:6px;` +
    `font-size:0.692em;font-weight:700;color:#111;font-family:Inter,sans-serif;` +
    `background:white;padding:1px 5px;border-radius:3px;white-space:nowrap;` +
    `box-shadow:0 1px 3px rgba(0,0,0,.2);border:1px solid #ddd;pointer-events:none">` +
    escapeHtml(callsign) +
    `</div>` +
    visual +
    // Status badge — absolute below box. Doesn't rotate or affect box.
    `<div style="position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:2px;` +
    `background:${badge};color:white;font-size:0.692em;font-weight:800;` +
    `padding:2px 10px;border-radius:4px;text-align:center;font-family:Inter,sans-serif;` +
    `white-space:nowrap;letter-spacing:.4px;box-shadow:0 1px 4px rgba(0,0,0,.3);pointer-events:none">` +
    label +
    `</div>` +
    // Owning tenant, under the PARTNER badge (mockup `_ctSpawnPartnerDrones`).
    // The badge says *that* an aircraft is foreign; this says *whose* — the
    // whole point of the sourceTenant field. Absolute like the others, so it
    // stays out of the marker's bounding box.
    (origin === 'partner' && partnerTenant
      ? `<div style="position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:22px;` +
        `font-size:0.615em;font-family:ui-monospace,monospace;color:${EXTERNAL_COLOR};` +
        `text-shadow:0 0 3px #fff,0 0 3px #fff;white-space:nowrap;pointer-events:none">` +
        `Partner: ${escapeHtml(partnerTenant)}` +
        `</div>`
      : '');
  return { html, boxSize, zIndex: droneZIndex(selected, hovered) };
}
